import graphql from "babel-plugin-relay/macro";
import { useS3UploadMutation, useS3UploadMutationResponse } from "generatedQueries/useS3UploadMutation.graphql";
import { useLogger } from "infrastructure/logger";
import { useRef, useEffect } from "react";
import { useMutation } from "react-relay";
import { PayloadError } from "relay-runtime";
import { getFileExtension } from "shared/fileUtils";

export function useS3Upload() {
    const logger = useLogger();
    const [commitMutation, isMutationInProgress] = useMutation<useS3UploadMutation>(mutation);
    const mutationDisposableRef = useRef<ReturnType<typeof commitMutation> | null>(null);
    const s3PostAbortControllerRef = useRef<AbortController | null>(null);

    const isPostToS3InProgress = !!s3PostAbortControllerRef.current;

    useEffect(() => {
        return () => {
            mutationDisposableRef.current?.dispose();
            s3PostAbortControllerRef.current?.abort();
        };
    }, []);

    return {
        upload,
    };

    function upload(file: File): Promise<string> {
        if (isMutationInProgress || isPostToS3InProgress) {
            return Promise.reject(new Error("An upload is already in progress"));
        }

        return new Promise<string>((resolve, reject) => {
            mutationDisposableRef.current = commitMutation({
                onCompleted: onMutationComplete,
                onError: onMutationError,
                variables: {
                    contentType: file.type,
                    fileExtension: getFileExtension(file) ?? "",
                },
            });

            function onMutationComplete(response: useS3UploadMutationResponse, errors: PayloadError[] | null): void {
                if (errors?.length) {
                    logger.error("Payload error when trying to generate upload URL", { PayloadErrors: errors });
                    reject(new Error("Payload error when trying to generate upload URL"));
                    return;
                }

                if (!response.generateUploadUrl) {
                    onMutationError(new Error("Received null response from GraphQL"));
                    return;
                }

                s3PostAbortControllerRef.current = new AbortController();
                postFileToS3(file, response.generateUploadUrl, s3PostAbortControllerRef.current.signal)
                    .then((objectKey) => {
                        logger.debug("Successfully uploaded file to S3", { ObjectKey: objectKey });
                        resolve(objectKey);
                    })
                    .catch((e: Error) => {
                        if (e.name === "AbortError") {
                            return;
                        }

                        logger.error("Failed to upload file to S3", { ErrorMessage: e.message }, e);
                        reject(e);
                    })
                    .finally(() => {
                        s3PostAbortControllerRef.current = null;
                    });
            }

            function onMutationError(error: Error): void {
                logger.error("Failed to generate upload URL", { ErrorMessage: error.message }, error);
                reject(error);
            }
        });
    }
}

async function postFileToS3(file: File, presignedResponse: NonNullable<useS3UploadMutationResponse["generateUploadUrl"]>, signal: AbortSignal): Promise<string> {
    const formData = new FormData();
    presignedResponse.formData.forEach(({ key, value }) => formData.append(key, value));
    formData.append("file", file);

    const response = await fetch(presignedResponse.url, { method: "POST", body: formData, signal });

    if (response.ok) {
        return presignedResponse.objectKey;
    }

    const errorText = await response.text();
    throw new Error(`Failed to POST file to S3: ${response.status} - ${errorText}`);
}

const mutation = graphql`
    mutation useS3UploadMutation($contentType: String!, $fileExtension: String!) {
        generateUploadUrl(contentType: $contentType, fileExtension: $fileExtension) {
            formData {
                key
                value
            }
            url
            objectKey
        }
    }
`;
